import { _, moment } from "../../vendor";

/**
 * @class Recurrencies
 * @author Felx Albroscheit
 * @see https://github.com/c-trimm/moment-recur
 *
 * Allows generating recurring events for a given timespan
 *
 * ATTENTION: Used plugin `moment-recur` has several known issues regarding
 * wrong predictment of dates or suddenly switches to UTC mode.
 * @see https://github.com/c-trimm/moment-recur/issues
 *
 * @todo Add tests
 * @todo eventually, use recurrence.except("01/03/2014") in
 *       combination with contingents/getAvails
 * @todo Just export a function ;)
 */
export default class {
  /**
   * @constructor
   * @param event { object }
   * @param then { function }
   * @param max { integer }
   * @param fromDate { date }
   * @param endDate { date }
   */
  constructor({
    event,
    max,
    fromDate,
    endDate,
    then,
  }: {
    event: any;
    max: number;
    then: any;
    fromDate: any;
    endDate?: any;
  }) {
    fromDate = typeof fromDate !== "undefined" ? fromDate : new Date();

    // @todo Take care of timezone -> Not possible as
    // event._timeFilter removes the +02:00 ... :(
    const that: any = this,
      actualEventStart: any = moment(event.start, "YYYY-MM-DD HH:mm")
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0),
      maxRecurrences: number | string = typeof max != "undefined" ? max : "16",
      everyWeekdays: any[] = this._eventWeekdaysToArray(event),
      parentStartMinutes: any = moment(event.start).diff(moment(event.start).startOf("day")),
      parentStartEndInterval: any = moment(event.end).diff(moment(event.start));

    let recurrence: any = moment(event.start);

    if (!_.isNull(event.end)) {
      if (!moment(event.end).isSameOrAfter(moment(event.start))) {
        return;
      }
      recurrence = recurrence.recur(event.end); // sets an end date
    } else {
      recurrence = recurrence.recur();
    }

    switch (event.rrule) {
      default:
      case "weekly":
        recurrence.every(event.separation_count).weeks();
        break;
      case "monthly":
        recurrence.every(event.separation_count).months();
        break;
      case "years":
        recurrence.every(event.separation_count).years();
        break;
    }

    if (typeof event.valid_to != "undefined" && event.valid_to != null) {
      if (moment(event.valid_to, "YYYY-MM-DD HH:mm").isBefore(fromDate)) {
        return;
      }
    }

    recurrence.fromDate(fromDate); // e.g "02/05/2014"

    /* Outlined this as making troubles together with maxRecurrences
           being set
        if (typeof endDate != 'undefined' && !_.isNull(endDate)) {
          if (!moment(endDate).isSameOrAfter(moment(fromDate))) {
            return;
          }
          recurrence.endDate(endDate); // e.g "02/05/2014"
        }*/

    _.each(recurrence.next(maxRecurrences), (rec: any, key: any): void => {
      // Exception case I: Outside of demanded timespan
      if (typeof endDate != "undefined" && !_.isNull(endDate)) {
        if (rec.isAfter(moment(endDate))) {
          return;
        }
      }

      // Exception case II: Event has not yet started in the generated timespan
      if (actualEventStart.isAfter(rec)) {
        return;
      }

      // Exception case III: End of event max. timespan reached
      if (typeof event.valid_to != "undefined" && event.valid_to != null) {
        if (moment(event.valid_to, "YYYY-MM-DD HH:mm").isBefore(rec)) {
          return;
        }
      }

      let result: any = {};

      result.id = event.id;
      result.parent_id = event.id;
      result.title = event.title;

      if (everyWeekdays.length > 1) {
        //console.log('every', everyWeekdays, rec.format('dd, DD MM'));

        let recurrenceWeekdays: any = rec.recur().every(everyWeekdays).daysOfWeek();

        _.each(recurrenceWeekdays.next(everyWeekdays.length), (_rec, _key) => {
          if (_rec.format("dd") == rec.format("dd")) {
            //console.log('skipped', _rec.format('dd, DD MM YY'));
            return;
          }

          //console.log('Got', _rec.format('dd, DD MM YY'));

          let _result: any = _.clone(result);

          _result.start = _rec.add(parentStartMinutes);
          _result.end = _result.start
            .clone()
            .add(parentStartEndInterval)
            .toISOString()
            .replace(".000Z", "+02:00");
          _result.start = _result.start.toISOString().replace(".000Z", "+02:00");

          then(_.clone(_result));
        });
      }

      result.start = rec.add(parentStartMinutes);

      /* if (result.start.toISOString().indexOf('Z') >= 0 
               // && typeof result._timeFixed == 'undefined') {
            ) {
                    // For those events having no appointments...
                    result.start.subtract(2,'hours');
                    result._timeFixed = true;
            }*/

      result.end = result.start
        .clone()
        .add(parentStartEndInterval)
        .toISOString()
        .replace(".000Z", "+02:00");
      result.start = result.start.toISOString().replace(".000Z", "+02:00");

      then(_.clone(result));
    });
  }

  _eventWeekdaysToArray(event: any): number[] {
    let result: number[] = [];

    if (
      !_.isUndefined(event.day_of_week_1) &&
      !_.isNull(event.day_of_week_1) &&
      event.day_of_week_1
    )
      result.push(1);
    if (
      !_.isUndefined(event.day_of_week_2) &&
      !_.isNull(event.day_of_week_2) &&
      event.day_of_week_2
    )
      result.push(2);
    if (
      !_.isUndefined(event.day_of_week_3) &&
      !_.isNull(event.day_of_week_3) &&
      event.day_of_week_3
    )
      result.push(3);
    if (
      !_.isUndefined(event.day_of_week_4) &&
      !_.isNull(event.day_of_week_4) &&
      event.day_of_week_4
    )
      result.push(4);
    if (
      !_.isUndefined(event.day_of_week_5) &&
      !_.isNull(event.day_of_week_5) &&
      event.day_of_week_5
    )
      result.push(5);
    if (
      !_.isUndefined(event.day_of_week_6) &&
      !_.isNull(event.day_of_week_6) &&
      event.day_of_week_6
    )
      result.push(6);
    if (
      !_.isUndefined(event.day_of_week_7) &&
      !_.isNull(event.day_of_week_7) &&
      event.day_of_week_7
    )
      result.push(7);

    return result;
  }
}
